import React from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';
import { useTheme } from '../theme';
import Txt from './Txt';

interface CardProps {
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

/** Card container — .t-card: card bg, 1px line border, r-3 radius, sm shadow. */
export function Card({ children, style }: CardProps) {
  const t = useTheme();
  return (
    <View
      style={[
        {
          backgroundColor: t.colors.card,
          borderWidth: 1,
          borderColor: t.colors.line,
          borderRadius: t.radii[3],
          ...t.shadows.sm,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

/** .t-card-head — padded row, space-between, bottom border. */
export function CardHead({ children, style }: CardProps) {
  const t = useTheme();
  return (
    <View
      style={[
        {
          paddingVertical: 12,
          paddingHorizontal: 16,
          borderBottomWidth: 1,
          borderBottomColor: t.colors.line,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

/** The h3 title inside a card head. */
export function CardTitle({ children }: { children: React.ReactNode }) {
  return <Txt weight="semibold">{children}</Txt>;
}

/** The muted sub-label inside a card head — .t-card-sub. */
export function CardSub({ children }: { children: React.ReactNode }) {
  return (
    <Txt color="ink3" size="sm">
      {children}
    </Txt>
  );
}

/** .t-card-body — 16px padding. */
export function CardBody({ children, style }: CardProps) {
  return <View style={[{ padding: 16 }, style]}>{children}</View>;
}

/** .t-card-foot — padded row on paper-2 with top border. */
export function CardFoot({ children, style }: CardProps) {
  const t = useTheme();
  return (
    <View
      style={[
        {
          paddingVertical: 12,
          paddingHorizontal: 16,
          borderTopWidth: 1,
          borderTopColor: t.colors.line,
          backgroundColor: t.colors.paper2,
          borderBottomLeftRadius: t.radii[3],
          borderBottomRightRadius: t.radii[3],
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

export default Card;
